<template>
  <div>
    <p class="cp-help-text">
      To build up your product start by selecting a product category, then more options are displayed.
      When you have finished the product selection click on the Next button. The product combination will now be checked against live print partners in the region. In case the combination is not available, try a different paper or finish, or contact our support.
    </p>
    <br>

    <template v-if="supportFeature('stock_product_support')">
        <div class="section-header">Product type</div>
        <div class="columns">
          <div class="column is-half">
            <b-field>
              <b-radio v-model="orderData.type"
                       native-value="pod">
                Print on Demand - Print file provided during the order process.
              </b-radio>
            </b-field>
            <b-field>
              <b-radio v-model="orderData.type"
                       native-value="stock">
                Stock item - The item is picked from production stock.
              </b-radio>
            </b-field>
        </div>
        </div>
    </template>

    <br>

    <div class="section-header">Select the product</div>

    <div class="columns">
      <div class="column is-half">
        <section>
          <b-field label="Cloudprinter product category"
                   custom-class="required-field"
                   :type="{'is-danger': errors.has('product_form.product category')}"
                   :message="errors.first('product_form.product category')">
            <b-select placeholder="Select the category"
                      expanded
                      data-vv-scope="product_form"
                      v-validate="'required'"
                      name="product category"
                      v-model="orderData.cp_product_category"
                      @change.native="onCategorySelect();">
              <option v-for="(category) in cpCategories"
                      :value="category.name"
                      :key="category.name">
                {{ category.name }}
              </option>
            </b-select>
          </b-field>
        </section>
      </div>

      <div class="column is-half" v-if="orderData.cp_product_category">
        <section>
          <b-field label="Cloudprinter product sub category"
                   custom-class="required-field"
                   :type="{'is-danger': errors.has('product_form.product category')}"
                   :message="errors.first('product_form.product category')">
            <b-select placeholder="Select the sub category"
                      expanded
                      data-vv-scope="product_form"
                      v-validate="'required'"
                      @change.native="onSubCategorySelect()"
                      name="product category"
                      v-model="orderData.cp_product_sub_category"
                     >
              <option v-for="(subCategory) in cpCategories.filter(c => { return c.name == orderData.cp_product_category}).shift().sub_categories"
                      :value="subCategory.name"
                      :key="subCategory.name">
                {{ subCategory.name }}
              </option>
            </b-select>
          </b-field>
        </section>
      </div>
    </div>
    <div class="columns">
      <div class="column is-half" v-if="orderData.cp_product_sub_category">
        <section>
          <b-field label="Cloudprinter product"
                   custom-class="required-field"
                   :type="{'is-danger': errors.has('product_form.product')}"
                   :message="errors.first('product_form.product')">
            <b-select placeholder="Select the product" expanded
                      v-model="orderData.cp_product_reference"
                      v-validate="'required'"
                      data-vv-scope="product_form"
                      name="product"
                      @input="onProductSelect($event)">
              <option
                      v-for="product in availableProducts()"
                      :value="product.product_reference"
                      :key="product.product_reference">
                {{ product.name }}
              </option>
            </b-select>
          </b-field>
        </section>
      </div>
    </div>

    <br>
    <template v-if="orderData.cp_product_reference && orderData.type !== 'stock'">
      <div class="section-header">
        Select product options
      </div>
      <div class="columns is-multiline">
      <div v-for="(option) in cpProductOptions"
           v-if="orderData.cp_product_reference"
           v-bind:key="option.key"
           class="column is-6">
        <template v-if="option.type == 'select'">
          <section>
            <b-field
                    custom-class="required-field"
                    v-bind:label="option.option_name"
                    :type="{'is-danger': errors.has('product_form.' + option.option_name.toLowerCase())}"
                    :message="errors.first('product_form.' + option.option_name.toLowerCase())">
              <b-select placeholder="Select the option" expanded
                        v-validate="'required'"
                        v-model="orderData.cp_product_options[option.key]"
                        data-vv-scope="product_form"
                        :name="option.option_name.toLowerCase()">
                <option
                        v-for="option_value in option.option_values"
                        :value="option_value.key"
                        :key="option_value.key">
                  {{ option_value.value }}
                </option>
              </b-select>
            </b-field>
          </section>
        </template>
        <template v-if="option.type == 'number'">
          <section>
            <b-field v-bind:label="option.option_name"
                     custom-class="required-field"
                     :type="{'is-danger': errors.has('product_form.' + option.option_name.toLowerCase())}"
                     :message="errors.first('product_form.' + option.option_name.toLowerCase())"
            >
              <b-input
                      v-model="orderData.cp_product_options[option.key]"
                      :name="option.option_name.toLowerCase()"
                      data-vv-scope="product_form"
                      v-validate="{required: true, numeric: true}"
              ></b-input>
            </b-field>
          </section>
        </template>
      </div>

      <div class="column is-6"
           v-if="orderData.cp_product_reference && isBookProductSelected()">
        <template>
          <section>
            <b-field label="Page count"
                     custom-class="required-field"
                     :type="{'is-danger': errors.has('product_form.page count')}"
                     :message="errors.first('product_form.page count')"
            >
              <b-input
                      v-model="orderData.cp_product_options['count_pages']"
                      name="page count"
                      data-vv-scope="product_form"
                      v-validate="{required: true, numeric: true}"
              ></b-input>
            </b-field>
          </section>
        </template>
      </div>
    </div>
    </template>

    <br>
    <div class="section-header" v-if="orderData.cp_product_reference">
      Select the shipping country
    </div>

    <div class="columns" v-if="orderData.cp_product_reference">
      <div class="column is-6">
        <b-field label="Country"
                 custom-class="required-field"
                 :type="{'is-danger': errors.has('product_form.country')}"
                 :message="errors.first('product_form.country')">
          <b-select placeholder="Select the country"
                    expanded
                    v-validate="'required'"
                    data-vv-scope="product_form"
                    name="country"
                    @change.native="getStates()"
                    v-model="orderData.shipping_address.country">
            <option v-for="country in orderData.cpCountries"
                    :value="country.country_reference"
                    :key="country.country_reference">
              {{ country.country_reference }} - {{ country.note }}
            </option>
          </b-select>
        </b-field>
      </div>

      <div class="column is-6" v-if="stateIsRequired()">
        <b-field label="State"
                 custom-class="required-field"
                 :type="{'is-danger': errors.has('product_form.state')}"
                 :message="errors.first('product_form.state')">
          <b-select placeholder="Select the state" expanded
                    v-model="orderData.shipping_address.state"
                    v-validate="'required'"
                    data-vv-scope="product_form"
                    name="state">
            <option v-for="state of orderData.cpStates"
                    :value="state.state_reference"
                    :key="state.state_reference">
              {{ state.note }}
            </option>
          </b-select>
        </b-field>
      </div>
    </div>
    <br>
    <div class="section-header" v-if="orderData.cp_product_reference">
      Set the quantity
    </div>
    <template v-if="orderData.cp_product_reference && quantityType == 'range'">
      <section>
        <b-field custom-class="required-field"
                 :type="{'is-danger': errors.has('product_form.quantity')}"
                 :message="errors.first('product_form.quantity')"
                 :label="'Quantity (' + orderData.cpProductInfo.quantities.range[0].min +'-'+ orderData.cpProductInfo.quantities.range[0].max + ')'">
          <b-input v-model="orderData.quantity"
                   v-validate="{required: true, numeric: true}"
                   data-vv-scope="product_form"
                   name="quantity"></b-input>
        </b-field>
      </section>
    </template>

    <template v-if="orderData.cp_product_reference && quantityType == 'fixed'">
      <section>
        <b-field label="Quantity - The number of copies"
                 custom-class="required-field"
                 :type="{'is-danger': errors.has('product_form.quantity')}"
                 :message="errors.first('product_form.quantity')">
          <b-select placeholder="Select the quantity"
                    expanded
                    data-vv-scope="product_form"
                    v-model="orderData.quantity"
                    v-validate="'required'"
                    name="quantity">
            <option
                    v-for="fixed in orderData.cpProductInfo.quantities.fixed"
                    :value="fixed"
                    :key="fixed">
              {{ fixed }}
            </option>
          </b-select>
        </b-field>
      </section>
    </template>
  </div>
</template>

<script>
import Cloudprinter from '@/services/CloudprinterService'
import AddressMixin from '@/mixins/AddressMixin'
import UserMixin from '@/mixins/UserMixin'
import _ from 'lodash'

export default {
  name: 'ProductSetup',
  mixins: [AddressMixin, UserMixin],
  data () {
    return {
      orderData: {},
      cpProducts: {},
      cpCategories: [],
      cpProductOptions: [],
      quantityType: false
    }
  },

  created () {
    this.orderData = this.$store.getters['cpOrder/allData']
    this.getProductsList()
    this.getCategoriesList()
    this.getCountryList()
  },

  watch: {
    'orderData.cpProductInfo': 'onProductEdit',
    'orderData.type': 'onTypeChanged'
  },

  methods: {
    getCategoriesList: function () {
      this.$store.dispatch('loader/show')
      Cloudprinter.getCategories().then((response) => {
        this.cpCategories = response.data.categories
        this.$store.dispatch('loader/hide')
      })
    },

    getProductsList: function () {
      this.$store.dispatch('loader/show')
      Cloudprinter.getProducts().then((response) => {
        this.cpProducts = response.data
      })
    },

    availableProducts: function () {
      return this.cpProducts.filter(product => {
        return product.sub_category == this.orderData.cp_product_sub_category
          && product.category == this.orderData.cp_product_category
      })
    },

    isBookProductSelected: function () {
      return ['Textbook BW', 'Textbook FC', 'Photobook', 'Brochure', 'Magazine', 'Card set'].indexOf(this.orderData.cp_product_category) !== -1
    },

    makeProductOptionsStructured: function () {
      if (_.isEmpty(this.orderData.cpProductInfo)) {
        return false
      }

      this.cpProductOptions = []
      let productOptions = {}
      for (let option of this.orderData.cpProductInfo.options) {
        if (['type_archive_total_files', 'type_total_pages'].indexOf(option.type) !== -1) {
          this.cpProductOptions.push({
            key: option.option_reference,
            option_name: option.type_name,
            type: 'number'
          })

          continue
        }

        if (!productOptions[option.type]) {
          productOptions[option.type] = {
            name: option.type_name,
            values: []
          }
        }

        productOptions[option.type].values.push({
          key: option.option_reference,
          value: option.note + (option.default === 1 ? ' (Standard)' : '')
        })
      }

      for (let key in productOptions) {
        this.cpProductOptions.push({
          key: key,
          option_name: productOptions[key].name,
          type: 'select',
          option_values: productOptions[key].values
        })
      }
    },

    onCategorySelect: function () {
      this.cpProductOptions = []
      this.orderData.priceCheckErrors = []
      this.orderData.cp_product_reference = null
      this.orderData.cp_product_sub_category = null
      this.orderData.priceCheckError = null
    },

    onSubCategorySelect: function () {
      this.cpProductOptions = []
      this.orderData.priceCheckErrors = []
      this.orderData.cp_product_reference = null
      this.orderData.priceCheckError = null
    },

    onProductSelect: function () {
      this.$store.dispatch('loader/show')
      this.cpProductOptions = []
      this.orderData.priceCheckErrors = []
      this.orderData.cp_product_options = {}
      this.orderData.quantity = null
      this.orderData.priceCheckError = null

      Cloudprinter.getProductInfo(this.orderData.cp_product_reference)
        .then((response) => {
          this.orderData.cpProductInfo = response.data
          this.getQuantityType()
          this.makeProductOptionsStructured()
          this.$store.dispatch('loader/hide')
        })
    },

    getQuantityType: function () {
      if (!_.isEmpty(this.orderData.cpProductInfo)) {
        if (_.has(this.orderData.cpProductInfo.quantities, 'fixed')) {
          this.quantityType = 'fixed'
        } else if (_.has(this.orderData.cpProductInfo.quantities, 'range')) {
          this.quantityType = 'range'
        }
      } else {
        this.quantityType = false
      }
    },

    onProductEdit: function () {
      this.makeProductOptionsStructured()
      this.getQuantityType()
      this.getStates()
    },

    onTypeChanged: function () {
      if (this.orderData.type === 'stock') {
        this.orderData.cp_product_options = {}
      }
    }
  }
}
</script>
